'use client';

import { useState, useEffect } from 'react';
import Link from 'next/link';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { SignupFormSchema } from '../../lib/register-schema';
import { useRouter } from 'next/navigation';
import { useFlash } from '../../lib/FlashMessageProvider';
import { Eye, EyeOff } from 'lucide-react';

function getFlashType(status) {
  if (status >= 200 && status < 300) return "success";
  if (status >= 400 && status < 500) return "warning";
  if (status >= 500) return "error";
  return "info";
}

export default function Register() {
  const router = useRouter();
  const { addMessage } = useFlash();

  const [termsAgreed, setTermsAgreed] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [privacyAgreed, setPrivacyAgreed] = useState(false);

  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors, isSubmitting },
    watch,
  } = useForm({
    resolver: zodResolver(SignupFormSchema),
    mode: 'onChange',
  });

  const password = watch('password');
  const confirmPassword = watch('confirm_password');

  useEffect(() => {
    const storedEmail = localStorage.getItem('email');
    if (storedEmail) setValue('email', storedEmail);
  }, [setValue]);

  const onSubmit = async (data) => {
  // Validate agreements before proceeding
  if (!termsAgreed || !privacyAgreed) {
    addMessage(
      "warning",
      "You must agree to the Terms and Conditions and Privacy Policy to continue."
    );
    return;
  }

  const { confirm_password, ...userData } = data;

  try {
    const res = await fetch(`${process.env.NEXT_PUBLIC_API_BASE_URL}/register`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(userData),
    });

    const responseData = await res.json().catch(() => ({}));
    const flashType = getFlashType(res.status);

    if (res.status === 201) {
      addMessage(flashType, responseData.message || "Registration Successful!");
      localStorage.setItem("otp_email", userData.email);
      router.push("/auth/otp-verification");
    } else {
      addMessage(flashType, responseData.message || "Registration failed");
    }
  } catch {
    addMessage("error", "Network error. Please try again.");
  }
};

  return (
    <div className="w-100 signup-container">
      <style>{`
        @media (max-width: 575.98px) {
          .signup-container {
            padding: 1rem !important;
          }
          .account {
            font-size: 1.4rem;
          }
          .custom-input {
            font-size: 14px;
            padding: 8px 10px;
          }
          .password-wrapper .eye-icon {
            top: 50%;
            transform: translateY(-50%);
          }
          .btn-submit {
            font-size: 15px;
            padding: 10px;
          }
          .auth-text {
            font-size: 13px;
          }
          label {
            font-size: 13px;
          }
        }

        @media (max-width: 374px) {
          .account {
            font-size: 1.2rem;
          }
          .custom-input {
            font-size: 13px;
          }
        }
      `}</style>
      <h3 className="mb-1 account">Create Account </h3>
      <p className="text-muted mb-4 fs-6">
        Ready to manage your payroll? Let's get you set up.
      </p>

      <form onSubmit={handleSubmit(onSubmit)} className="login-form">

        <div className="row">

          {/* FIRST NAME */}
          <div className="col-12 col-sm-6 form-group">
            <label>First Name</label>
            <input
              type="text"
              className={`custom-input ${errors.first_name ? 'error' : ''}`}
              {...register('first_name')}
            />
            {errors.first_name && <span className="error-text">{errors.first_name.message}</span>}
          </div>

          {/* LAST NAME */}
          <div className="col-12 col-sm-6 form-group">
            <label>Last Name</label>
            <input
              type="text"
              className={`custom-input ${errors.last_name ? 'error' : ''}`}
              {...register('last_name')}
            />
            {errors.last_name && <span className="error-text">{errors.last_name.message}</span>}
          </div>

          {/* ORG */}
          <div className="col-12 form-group">
            <label>Organisation Name</label>
            <input
              type="text"
              className={`custom-input ${errors.org_name ? 'error' : ''}`}
              {...register('org_name')}
            />
            {errors.org_name && <span className="error-text">{errors.org_name.message}</span>}
          </div>

          {/* EMAIL */}
          <div className="col-12 form-group">
            <label>Email</label>
            <input
              type="email"
              className={`custom-input ${errors.email ? 'error' : ''}`}
              {...register('email')}
            />
            {errors.email && <span className="error-text">{errors.email.message}</span>}
          </div>

          {/* PASSWORD */}
          <div className="col-12 form-group">
            <label>Password</label>
            <div className="password-wrapper">
              <input
                type={showPassword ? "text" : "password"}
                className={`custom-input ${errors.password ? 'error' : ''}`}
                {...register('password')}
              />
              <span className="eye-icon" onClick={() => setShowPassword(!showPassword)}>
                {showPassword ? <EyeOff size={18}/> : <Eye size={18}/>}
              </span>
            </div>
            {errors.password && <span className="error-text">{errors.password.message}</span>}
          </div>

          {/* CONFIRM PASSWORD */}
          <div className="col-12 form-group">
            <label>Confirm Password</label>
            <div className="password-wrapper">
              <input
                type={showConfirmPassword ? "text" : "password"}
                className={`custom-input ${
                  errors.confirm_password || (confirmPassword && password !== confirmPassword)
                    ? 'error'
                    : ''
                }`}
                {...register('confirm_password')}
              />
              <span className="eye-icon" onClick={() => setShowConfirmPassword(!showConfirmPassword)}>
                {showConfirmPassword ? <EyeOff size={18}/> : <Eye size={18}/>}
              </span>
            </div>

            {(errors.confirm_password || (confirmPassword && password !== confirmPassword)) && (
              <span className="error-text">
                {errors.confirm_password?.message || "Passwords do not match"}
              </span>
            )}
          </div>

        </div>
        

      {/* TERMS */}
      <div className="mt-2 mb-3">
      <label className="d-flex align-items-center gap-2 mb-2" style={{ cursor: "pointer" }}>
        <input
          type="checkbox"
          checked={termsAgreed}
          onChange={() => setTermsAgreed(!termsAgreed)}
        />
        <span>
          I agree to{" "}
          <Link href="/terms-of-use" className="forgot-text">
            Terms and Conditions
          </Link>
        </span>
      </label>

        <div className="d-flex align-items-center gap-2">
          <input
            type="checkbox"
            checked={privacyAgreed}
            onChange={() => setPrivacyAgreed(!privacyAgreed)}
          />
          <span>
            I agree to{" "}
            <Link href="/privacy" className="forgot-text">
              Privacy Policy
            </Link>
          </span>
        </div>
      </div>


      {/* BUTTON */}
      <button
        type="submit"
        className="btn-submit w-100 mb-2"
        disabled={!termsAgreed || !privacyAgreed || isSubmitting}
        style={{
          backgroundColor:
            !termsAgreed || !privacyAgreed || isSubmitting
              ? "#d1d5db"
              : "#0d6efd", 
          color:
            !termsAgreed || !privacyAgreed || isSubmitting
              ? "#6b7280"
              : "#fff",
          cursor:
            !termsAgreed || !privacyAgreed || isSubmitting
              ? "not-allowed"
              : "pointer",
          transition: "all 0.25s ease",
        }}
      >
        {isSubmitting ? "Creating Account..." : "Create Account"}
      </button>
      {(!termsAgreed || !privacyAgreed) && (
        <p
          style={{
            fontSize: "13px",
            color: "#6b7280",
            marginTop: "8px",
            marginBottom: "12px",
          }}
        >
          Please accept the Terms & Conditions and Privacy Policy to continue.
        </p>
      )}
              {/* LINKS */}
              <p className="auth-text mt-3">
                Already have an account? <Link href="/auth/login">Login now</Link>
              </p>

              <p className="auth-text">
                Are you an employee? <Link href="/auth/employee-login">Sign in</Link>
              </p>

            </form>
          </div>
        );
      }